import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { HiOutlineLockClosed, HiChevronDown } from "react-icons/hi";

import { useOwnerScopeContext } from "../../contexts/OwnerScopeContext";
import { useAuth } from "../../hooks";
import { useTeamSettings } from "../../hooks/useTeamSettings";
import { OWNER_SCOPE_VALUES, isOwnCsmScope } from "../../utils/ownerScope";
import { getScopeOptions, resolveEffectiveScope } from "./scopeOptions";

// Labels stay in this component's English voice, like the option labels in scopeOptions.
const SHOWING_LABEL = "Showing";
const MINE_LABEL = "My Customers";
const UNKNOWN_CSM_LABEL = "Selected CSM";
const LOCKED_HINT = "Your team shows each CSM only their own customers.";
// The fact only. "Your choice is unchanged" is untrue when a stored own entry moves to
// "My Customers"; "the same customers" is untrue when the stored CSM has left the list.
const LOAD_ERROR_HINT = "Couldn't load the CSM list.";

const isCsmScope = (token) =>
  typeof token === "string" && token.startsWith("csm:");

// Collapsed chip text: "All", "Me", or the CSM's initials.
const chipText = (ownerScope, label) => {
  if (ownerScope === OWNER_SCOPE_VALUES.MINE) return "Me";
  if (!isCsmScope(ownerScope)) return "All";
  const initials = label
    .replace(/\(.*\)/, "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return initials || "CS";
};

const EYEBROW_CLASS =
  "text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const HINT_CLASS =
  "mt-1 whitespace-normal text-xs leading-snug text-gray-500 dark:text-gray-400";

/**
 * The owner-scope switcher. Lives at the top of the left nav (PAN-425), above the page
 * links, so the subset being shown is visible on every page and reads as the container
 * for everything below it.
 *
 * - Expanded: one line, "Showing" + a borderless native select styled like a nav item.
 * - `collapsed`: a chip naming the scope; clicking calls `onExpand` when `canExpand`,
 *   otherwise (mobile, where the sidebar cannot open) opens a popover — portalled to
 *   `document.body` because the nav clips overflow — holding the same control.
 * - Restricted-mode non-admins cannot change scope; they see a locked "My Customers"
 *   with a one-line reason instead of nothing.
 * - A failed CSM-list fetch never rewrites the stored scope (a stale-scope cleanup on a
 *   transient error would silently widen an admin's view to the whole team), except to move
 *   the current user's own entry to "mine", which needs no list (PAN-592) and never widens
 *   the view: "mine" is the user's own accounts, and a stored "csm:<own id>" is either those
 *   same accounts (an admin in the CSM list) or refused by the server (anyone else).
 * - While loading, the block keeps its height so the page links do not jump.
 */
export const CSMScopeFilter = ({
  collapsed = false,
  canExpand = true,
  onExpand,
}) => {
  const { ownerScope, setOwnerScope } = useOwnerScopeContext();
  const { apiClient, team, userId } = useAuth();
  const { settings, loading: settingsLoading } = useTeamSettings();

  const [csms, setCsms] = useState([]);
  const [csmsLoading, setCsmsLoading] = useState(true);
  const [csmsError, setCsmsError] = useState(false);
  const [popover, setPopover] = useState(null); // {top, left} while open
  const chipRef = useRef(null);
  const popoverRef = useRef(null);

  const isAdmin = team?.is_admin ?? false;
  const csmViewMode = settings?.csm_view_mode ?? "open";

  const fetchCsms = useCallback(async () => {
    setCsmsLoading(true);
    setCsmsError(false);
    try {
      const response = await apiClient.get("/csms/");
      setCsms(response.data?.results ?? response.data ?? []);
    } catch {
      setCsms([]);
      setCsmsError(true);
    } finally {
      setCsmsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchCsms();
  }, [fetchCsms]);

  useEffect(() => {
    if (csmsLoading || settingsLoading) return;
    if (csmsError) {
      // Resolution needs the real CSM list: with an errored (empty) list a stored
      // "csm:<id>" would be mistaken for stale and persisted as "all". The one exception is
      // the current user's own entry, admin or not (PAN-592): moving it to "mine" needs no
      // list and cannot widen the view.
      if (isOwnCsmScope(ownerScope, userId)) {
        setOwnerScope(OWNER_SCOPE_VALUES.MINE);
      }
      return;
    }

    const csmIds = csms.map((c) => c.id);
    const effective = resolveEffectiveScope({
      stored: ownerScope,
      isAdmin,
      csmViewMode,
      csmIds,
      currentUserId: userId,
    });

    if (effective !== ownerScope) {
      setOwnerScope(effective);
    }
  }, [
    csmsLoading,
    settingsLoading,
    csmsError,
    csms,
    isAdmin,
    csmViewMode,
    userId,
    ownerScope,
    setOwnerScope,
  ]);

  const closePopover = useCallback(() => setPopover(null), []);

  useEffect(() => {
    if (!collapsed) closePopover();
  }, [collapsed, closePopover]);

  useEffect(() => {
    if (!popover) return undefined;
    const onKey = (e) => e.key === "Escape" && closePopover();
    const onDown = (e) => {
      if (
        !popoverRef.current?.contains(e.target) &&
        !chipRef.current?.contains(e.target)
      ) {
        closePopover();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", closePopover);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", closePopover);
    };
  }, [popover, closePopover]);

  const options = getScopeOptions({
    isAdmin,
    csmViewMode,
    csms,
    currentUserId: userId,
  });
  const locked = options.length === 0; // restricted mode, non-admin: always "mine"
  // A stored csm:<own id> has no option of its own (PAN-592) and is about to become
  // "mine"; show it as "My Customers" until it does (the same accounts while the admin is in
  // the CSM list; otherwise the stored value is refused by the server anyway).
  const displayScope = isOwnCsmScope(ownerScope, userId)
    ? OWNER_SCOPE_VALUES.MINE
    : ownerScope;
  const selected = options.find((opt) => opt.value === displayScope);
  const currentLabel = locked
    ? MINE_LABEL
    : (selected?.label ?? (isCsmScope(ownerScope) ? UNKNOWN_CSM_LABEL : ""));
  // Not resolved yet: loading, or the first paint before the resolution effect runs.
  const pending = csmsLoading || settingsLoading || !currentLabel;

  if (pending) {
    return collapsed ? (
      <div aria-busy="true" className="my-2 flex h-8 w-full justify-center">
        <span className="h-5 w-8 animate-pulse rounded-md bg-gray-100 dark:bg-gray-800" />
      </div>
    ) : (
      <div
        aria-busy="true"
        className="mb-2 whitespace-normal border-b border-gray-200 px-5 pb-3 pt-1 dark:border-gray-700"
      >
        <span className={EYEBROW_CLASS}>{SHOWING_LABEL}</span>
        <div className="mt-1 h-5 w-32 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    );
  }

  const isFiltered = locked || ownerScope !== OWNER_SCOPE_VALUES.ALL;
  const title = `${SHOWING_LABEL}: ${currentLabel}`;

  const control = locked ? (
    <span
      data-testid="csm-scope-filter-locked"
      title={LOCKED_HINT}
      className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-200"
    >
      <HiOutlineLockClosed size="14" className="opacity-60" aria-hidden />
      {MINE_LABEL}
    </span>
  ) : (
    <span className="flex min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 focus-within:ring-2 focus-within:ring-blue-500 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700">
      <span className="relative flex min-w-0 flex-1 items-center">
        <select
          id="csm-scope-filter"
          data-testid="csm-scope-filter"
          aria-label={SHOWING_LABEL}
          title={currentLabel}
          value={selected ? displayScope : ""}
          onChange={(e) => setOwnerScope(e.target.value)}
          style={{ backgroundImage: "none" }}
          className="w-full min-w-0 cursor-pointer appearance-none truncate border-0 bg-transparent py-0 pl-0 pr-5 text-sm font-medium text-gray-800 focus:ring-0 dark:text-gray-100"
        >
          {!selected && <option value="">{currentLabel}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <HiChevronDown
          size="16"
          aria-hidden
          className="pointer-events-none absolute right-0 text-gray-500"
        />
      </span>
    </span>
  );

  const hints = (
    <>
      {locked && <p className={HINT_CLASS}>{LOCKED_HINT}</p>}
      {csmsError && !locked && (
        <p data-testid="csm-scope-filter-error" className={HINT_CLASS}>
          {LOAD_ERROR_HINT}
        </p>
      )}
    </>
  );

  if (collapsed) {
    const openPopover = () => {
      const rect = chipRef.current?.getBoundingClientRect();
      setPopover({ top: rect?.top ?? 0, left: (rect?.right ?? 0) + 8 });
    };
    return (
      <>
        <button
          ref={chipRef}
          type="button"
          data-testid="csm-scope-filter-collapsed"
          title={title}
          aria-label={title}
          aria-expanded={canExpand ? undefined : Boolean(popover)}
          onClick={() =>
            canExpand ? onExpand?.() : popover ? closePopover() : openPopover()
          }
          className="my-2 flex h-8 w-full items-center justify-center"
        >
          <span
            data-testid="csm-scope-filter-chip"
            className={
              isFiltered
                ? "rounded-md bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white"
                : "rounded-md border border-gray-300 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-200"
            }
          >
            {chipText(displayScope, currentLabel)}
          </span>
        </button>
        {popover &&
          createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={SHOWING_LABEL}
              data-testid="csm-scope-filter-popover"
              style={{
                position: "fixed",
                top: popover.top,
                left: popover.left,
              }}
              className="z-50 w-56 whitespace-normal rounded-lg border bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900"
            >
              <span className={EYEBROW_CLASS}>{SHOWING_LABEL}</span>
              <div className="mt-1">{control}</div>
              {hints}
            </div>,
            document.body,
          )}
      </>
    );
  }

  return (
    <div className="mb-2 whitespace-normal border-b border-gray-200 px-5 pb-3 pt-1 dark:border-gray-700">
      <div className="flex min-w-0 items-center gap-2">
        <span className={EYEBROW_CLASS}>{SHOWING_LABEL}</span>
        {control}
      </div>
      {hints}
    </div>
  );
};
